// Moved from `src/hooks/query/useQuerySessionContextFiles.ts` (now a shim)
// at D5, ARCFE-990. Fully headless: seam token (S2), seam queryClient for
// the module commands, `normalizedStringCompare` promoted to `@heidi/utils`.
// The progress-carryover in queryFn is preserved exactly — refetches keep
// each file's upload progress by name-or-id match against the old cache.
import { normalizedStringCompare } from '@heidi/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GET_SESSION_CONTEXT_FILES,
  GET_SESSION_CONTEXT_FILES_PROGRESS,
  type QuerySessionContextFile,
  type SessionContextFileProgress,
} from '../../contracts';
import { getContextDocuments } from '../api/documents';
import { getSessionDataConfig } from '../config';

export { GET_SESSION_CONTEXT_FILES, GET_SESSION_CONTEXT_FILES_PROGRESS };
export type { QuerySessionContextFile, SessionContextFileProgress };

export const getSessionContextFilesFromCache = (sessionId: string) => {
  return getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionContextFile[]>([GET_SESSION_CONTEXT_FILES, { sessionId }]);
};

export const invalidateContextFiles = async (sessionId: string) => {
  await getSessionDataConfig()
    .getQueryClient()
    .invalidateQueries({ queryKey: [GET_SESSION_CONTEXT_FILES, { sessionId }] });
};

export function useQuerySessionContextFiles(sessionId: string) {
  const queryClient = useQueryClient();
  const token = getSessionDataConfig().useAuthToken?.();

  return useQuery<QuerySessionContextFile[] | undefined>({
    queryKey: [GET_SESSION_CONTEXT_FILES, { sessionId }],
    enabled: !!token && !!sessionId,
    queryFn: async () => {
      const { data } = await getContextDocuments(sessionId);
      const oldData = queryClient.getQueryData<QuerySessionContextFile[]>([
        GET_SESSION_CONTEXT_FILES,
        { sessionId },
      ]);
      const sortedData = [...data].reverse();
      const contextFiles = sortedData.map((d) => {
        const cachedFile =
          oldData?.find((f) => d.id === f.id) ??
          oldData?.find(
            (f) => !f.id.startsWith('temp_') && normalizedStringCompare(d.name, f.name)
          );
        return {
          ...d,
          loadingProgress: cachedFile?.loadingProgress || undefined,
          uploadTrackingId: cachedFile?.uploadTrackingId,
        };
      });
      const activeOptimisticFiles = oldData?.filter(
        (cachedFile) =>
          cachedFile.uploadTrackingId === cachedFile.id &&
          !cachedFile.status &&
          !sortedData.some((serverFile) => serverFile.id === cachedFile.id)
      );

      return [...(activeOptimisticFiles ?? []), ...contextFiles];
    },
  });
}

export function useQuerySessionContextFileProgress(sessionId: string) {
  return useQuery<SessionContextFileProgress[] | undefined>({
    queryKey: [GET_SESSION_CONTEXT_FILES_PROGRESS, { sessionId }],
    queryFn: () => {
      return [];
    },
  });
}
